import React from 'react';
import {List} from 'immutable';
import {apiV2} from 'sources/api';
import urlWithParams from 'utils/urlWithParams';
import debounce from 'lodash/debounce';
import Popover from 'components/pure/utils/Popover';
import {EXTERNAL_SHARE_ALLOW_ALL, EXTERNAL_SHARE_PREVENT_ALL} from 'utils/const';
import 'styles/sharing/DirectCallSharingDialog.styl';
import SharingFormActions from 'actions/sharing/SharingFormActions';

import fetchMissingUsers from 'utils/fetchMissingUsers';
import {TeamIcon} from '@components/icon/TeamIcon';
import {DepartmentIcon} from '@components/icon/DepartmentIcon';
import {ShareTargetInput} from 'components/pure/sharing/direct-share/dialog/ShareTargetInput';

const searchPromise = (input, addTeams) =>
  apiV2
    .get(
      urlWithParams('/users/autocomplete', {
        for_sharing: 'True',
        add_teams: addTeams ? 'True' : '', // Any non-empty string is true for the BE
        q: input,
      })
    )
    .then(resp => resp.data.users)
    .then(results => {
      const ids = results.filter(result => result.type === 'user').map(result => result.id);
      // fire and forget. hope for the best.
      fetchMissingUsers(ids);
      return results;
    });

const doSearch = debounce(
  (input, addTeams, success, final) => searchPromise(input, addTeams).then(success).finally(final),
  300
);

/**
 * FileUploadSharingDialogFormSharingTarget uses the `react-selectize` framework to implement
 * a <Select /> dropdown that allows a user to type / select multiple users and emails as the sharing
 * targets. Notice that the auto-complete feature is implemented asynchronously using the backend's
 * endpoint
 */
class FileUploadSharingDialogFormSharingTargets extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      options: [],
      search: '',
    };
  }

  /**
   * Load async options from the backend and update state
   */
  loadAsyncOptions(input) {
    if (!input.trim()) {
      this.setState({options: [], loading: false});
      return;
    }

    this.setState({loading: true});
    return doSearch(
      input,
      this.props.addTeams,
      data => {
        const entities = List(data);
        // an entity can be a user, a team or a department
        const sharingTargetsOptions = entities
          .map(entity => {
            return {
              value: entity.id,
              type: entity.type,
              label:
                (entity.type === 'department'
                  ? 'Department: '
                  : entity.type === 'team'
                  ? 'Team: '
                  : '') + entity.name,
              id: entity.id,
              avatar:
                entity.type === 'department'
                  ? DepartmentIcon
                  : entity.type === 'team'
                  ? TeamIcon
                  : null,
            };
          })
          .sortBy(entity => entity.label);
        this.setState({
          loading: false,
          options: sharingTargetsOptions.toArray(),
        });
      },
      () =>
        this.setState({
          loading: false,
        })
    );
  }

  /**
   * Update `this.props.sharingTargets`
   * @param  {array} values An array of {value: %(value)s, label: %(label)s} objects
   */
  updateSharingTargets(values) {
    // filter out those "you can't do such and such" options related to external shares
    values = values.filter(v => v.value !== null);
    const sharingTargets = values
      .map(value => {
        if (value.id) {
          return value.id;
        } else if (value.value) {
          return value.value;
        } else {
          console.error('Unhandled value:', value);
          return null;
        }
      })
      .filter(v => v !== null);
    if (this.props.updateSharingTargets) {
      this.props.updateSharingTargets(List(sharingTargets));
    }
    if (this.props.updateSharingValues) {
      this.props.updateSharingValues(values);
    }
    SharingFormActions.updateSharingTargets(sharingTargets);
    SharingFormActions.updateSharingValues(values);

    if (this.props.onDataChanged) {
      this.props.onDataChanged('targets');
    }
  }

  onSearchChange = term => {
    this.loadAsyncOptions(term);
    this.setState({search: term});
  };

  onValuesChange = values => {
    this.updateSharingTargets(values);
    this.setState({options: [], search: ''});
  };

  render() {
    const department = this.props.department;
    const extSharePermission =
      this.props.checkExternalShares === false
        ? EXTERNAL_SHARE_ALLOW_ALL
        : department
        ? department.externalSharesPermission
        : EXTERNAL_SHARE_PREVENT_ALL;

    const placeholder = this.props.addTeams
      ? "Start typing user's name or email, or team/department name"
      : "Start typing user's name or email";

    // See similar code in FileUploadSharingDialogFormComments for more comments on the next few lines
    const {errorAnchor} = this.props;
    const anchor = errorAnchor || this.targetsSelect;
    const label = errorAnchor ? '' : 'targets';

    return (
      <div className="targets-select">
        <ShareTargetInput
          search={this.state.search}
          onChange={this.onValuesChange}
          values={this.props.sharingValues}
          onSearchChange={this.onSearchChange}
          options={this.state.options}
          placeholder={placeholder}
          loading={this.state.loading}
          externalSharePermission={extSharePermission}
          error={this.props.error}
        />
        <div className="error-anchor" ref={c => (this.targetsSelect = c)} />
        <Popover
          open={!!this.props.error}
          canAutoPosition={false}
          useLayerForClickAway={false}
          anchorEl={anchor}
          anchorOrigin={{horizontal: 'right', vertical: 'center'}}
          targetOrigin={{horizontal: 'left', vertical: 'center'}}>
          <div
            className="sharing-dialog-popover-error-message"
            ref={c => this.props.onPopoverRef(label, c)}>
            {this.props.error}
            {this.props.errorAnchor && <div className="more-errors-up"> (Please scroll up)</div>}
          </div>
        </Popover>
      </div>
    );
  }
}

export default FileUploadSharingDialogFormSharingTargets;
